import { parseDuration, type DurationUnit, type Task, type TaskStatus } from '@forecastify/shared';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A GRID CELL WAS EDITED. One definition, two callers — item 30.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EXTRACTED SO THE PROJECT'S OWN TAB CAN HAVE THE GRID.** *"Project tasks gain the kanban /
 * grid / timeline / list switch the global screen has."* A second copy of this function in the
 * project route is the drift `task-columns.ts` was extracted to prevent one level down: the copy
 * nobody opens is the one that stops resolving a status somebody renamed.
 *
 * ⚠️ **The grid speaks TEXT and the API speaks ids**, so a status edit arrives as a name and has to
 * be resolved. An unknown name is refused HERE with the value in the message — sending it on would
 * produce a validation error naming a uuid field the person never saw.
 */
export async function applyGridEdit(
  task: Task,
  column: string,
  value: string,
  deps: {
    readonly statuses: readonly TaskStatus[];
    readonly members: readonly { id: string; name: string }[];
    /* ⚠️ `Promise<unknown>`, not `Promise<boolean | void>`: the two callers return different
       things — one a `boolean`, one nothing — and `void` in a union is a lint error and a type
       nobody can narrow. Nothing here reads the result. */
    readonly patch: (task: Task, body: Record<string, unknown>) => Promise<unknown>;
    /** ⚠️ Already translated by the caller — this file names no message keys. */
    readonly onUnknown: (message: string) => void;
    readonly t: Translate;
    /** ⚠️ 3 Nov 2026 · the typist's unit — an estimate typed `1,5` is a day and a half (DU-1). */
    readonly unit: DurationUnit;
  },
): Promise<void> {
  const { statuses, members, patch, onUnknown, t, unit } = deps;

  if (column === 'title') {
    await patch(task, { title: value });
    return;
  }
  if (column === 'startsOn' || column === 'dueOn') {
    await patch(task, { [column]: value === '' ? null : value });
    return;
  }
  if (column === 'estimate') {
    /* Parsed with the same function the server uses, so the two cannot disagree. The screen has
     * already refused an unparseable value; this is the boundary that turns it into hours. */
    const hours = value.trim() === '' ? 0 : parseDuration(value, unit);
    if (hours === null) return;
    await patch(task, { estimateHours: hours });
    return;
  }
  if (column === 'status') {
    const match = statuses.find(
      (status) => status.name.toLowerCase() === value.trim().toLowerCase(),
    );
    if (match === undefined) {
      onUnknown(t('tasks.grid.unknownStatus', { value }));
      return;
    }
    await patch(task, { statusId: match.id });
    return;
  }
  if (column === 'assignee') {
    /* ⚠️ **An empty cell UNASSIGNS**, which is a real edit and not a no-op — the grid offers a
     * blank first option for exactly this. */
    if (value.trim() === '') {
      await patch(task, { assigneeMemberId: null });
      return;
    }
    const match = members.find(
      (person) => person.name.toLowerCase() === value.trim().toLowerCase(),
    );
    if (match === undefined) {
      onUnknown(t('tasks.grid.unknownAssignee', { value }));
      return;
    }
    await patch(task, { assigneeMemberId: match.id });
  }
}
